import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import { toast } from "react-toastify";
import { normalizeMetadataFilterDateForApi } from "helpers/metadataFilterHelpers";

import CommonTable from "components/Common/TableContainer";
import Loaders from "components/Common/Loaders";
import { submitNotInterestedReportFilterData } from "model/NotInterestedReportModel";

const BASE_TABLE_COLUMNS = [
    { Header: "SN", accessor: "sn" },
    { Header: "Client", accessor: "client" },
    { Header: "Not Interested Count", accessor: "notInterestedCount" },
];

const buildReasonAccessor = (index) => `reason_${index}`;

const normalizeReasonId = (value) => String(value ?? "").trim();

const extractReasonColumnDefinitions = (reasonColumns = []) =>
    (Array.isArray(reasonColumns) ? reasonColumns : [])
        .map((reasonColumn = {}) => ({
            reasonsId: reasonColumn?.reasonsId ?? reasonColumn?.reasonId ?? "",
            reasonsTitle: String(
                reasonColumn?.reasonsTitle ?? reasonColumn?.reasonTitle ?? ""
            ).trim(),
        }))
        .filter(
            (definition) =>
                definition.reasonsTitle ||
                normalizeReasonId(definition.reasonsId) !== ""
        );

const buildTableColumns = (reasonColumnDefinitions = []) => {
    const reasonColumns = (Array.isArray(reasonColumnDefinitions)
        ? reasonColumnDefinitions
        : []
    ).map((definition, index) => ({
        Header: definition.reasonsTitle || `Reason ${index + 1}`,
        accessor: buildReasonAccessor(index),
        reasonsId: definition.reasonsId,
    }));

    return [...BASE_TABLE_COLUMNS, ...reasonColumns];
};

const resolveReasonCountValue = (reasonColumn = {}) => {
    if (reasonColumn?.hasValue === false) return "";
    const count = reasonColumn?.count;
    if (count === null || count === undefined) return "";
    return count;
};

const mapRowsFromApi = (rows = [], reasonColumnDefinitions = []) => {
    const definitions = Array.isArray(reasonColumnDefinitions)
        ? reasonColumnDefinitions
        : [];
    const reasonIdToIndex = new Map(
        definitions.map((definition, index) => [
            normalizeReasonId(definition.reasonsId),
            index,
        ])
    );

    return (Array.isArray(rows) ? rows : []).map((item = {}, index) => {
        const mappedRow = {
            ...item,
            sn: item?.sn ?? item?.serialNumber ?? item?.sN ?? index + 1,
            client: item?.client ?? item?.clientName ?? "",
            clientId: item?.clientId ?? "",
            notInterestedCount:
                item?.notInterestedCount ??
                item?.not_interested_count ??
                item?.notInterested ??
                "",
        };

        definitions.forEach((_, reasonIndex) => {
            mappedRow[buildReasonAccessor(reasonIndex)] = "";
        });

        (Array.isArray(item?.reasonColumns) ? item.reasonColumns : []).forEach(
            (reasonColumn = {}) => {
                const reasonIndex = reasonIdToIndex.get(
                    normalizeReasonId(reasonColumn?.reasonsId ?? reasonColumn?.reasonId)
                );
                if (reasonIndex === undefined) return;
                mappedRow[buildReasonAccessor(reasonIndex)] =
                    resolveReasonCountValue(reasonColumn);
            }
        );

        return mappedRow;
    });
};

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data && typeof responseData.data === "object"
            ? responseData.data
            : responseData;

    const rows = Array.isArray(nestedData?.rows)
        ? nestedData.rows
        : Array.isArray(responseData?.rows)
            ? responseData.rows
            : Array.isArray(nestedData?.data)
                ? nestedData.data
                : Array.isArray(responseData?.data)
                    ? responseData.data
                    : Array.isArray(nestedData?.records)
                        ? nestedData.records
                        : Array.isArray(responseData?.records)
                            ? responseData.records
                            : Array.isArray(responseData)
                                ? responseData
                                : [];

    const reasonColumns = Array.isArray(nestedData?.reasonColumns)
        ? nestedData.reasonColumns
        : Array.isArray(responseData?.reasonColumns)
            ? responseData.reasonColumns
            : [];

    return { rows, reasonColumns };
};

const NotInterestedReport = () => {
    const [filterForm, setFilterForm] = useState({
        fromDate: "",
        toDate: "",
    });
    const [reportData, setReportData] = useState([]);
    const [reasonColumnDefinitions, setReasonColumnDefinitions] = useState([]);
    const [loading, setLoading] = useState(false);

    const filteredData = useMemo(() => reportData, [reportData]);
    const tableColumns = useMemo(
        () => buildTableColumns(reasonColumnDefinitions),
        [reasonColumnDefinitions]
    );

    const buildFilterPayload = () => ({
        dateFrom: normalizeMetadataFilterDateForApi(filterForm?.fromDate),
        dateTo: normalizeMetadataFilterDateForApi(filterForm?.toDate),
    });

    const getNotInterestedReportList = async () => {
        setLoading(true);
        try {
            const response = await submitNotInterestedReportFilterData(buildFilterPayload());
            const { rows, reasonColumns } = extractFilterResponse(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                setReasonColumnDefinitions([]);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            const nextReasonColumnDefinitions = extractReasonColumnDefinitions(reasonColumns);
            setReasonColumnDefinitions(nextReasonColumnDefinitions);
            setReportData(mapRowsFromApi(rows, nextReasonColumnDefinitions));
        } catch (error) {
            toast.error(error?.message || "Unable to load not interested report");
            setReportData([]);
            setReasonColumnDefinitions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    return (
        <React.Fragment>
            <div className="page-content session-activity-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Not Interested Report</h5>
                            <hr />

                            <LegacyFilterToolbar className="w-100">
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end session-activity-filter-row w-100">
                                    <Col xs="12" sm="6" lg="4" className="session-activity-filter-col">
                                        <span className="manage-user-filter-label">From date</span>
                                        <Input
                                            type="date"
                                            name="fromDate"
                                            className={LEGACY_FILTER_INPUT_CLASS}
                                            value={filterForm?.fromDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                    </Col>
                                    <Col xs="12" sm="6" lg="4" className="session-activity-filter-col">
                                        <span className="manage-user-filter-label">To date</span>
                                        <Input
                                            type="date"
                                            name="toDate"
                                            className={LEGACY_FILTER_INPUT_CLASS}
                                            value={filterForm?.toDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                    </Col>
                                    <Col xs="12" sm="6" lg="4" className="session-activity-filter-col">
                                        <span
                                            className="manage-user-filter-label"
                                            aria-hidden="true"
                                        >
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={getNotInterestedReportList}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                    </Col>
                                </Row>
                            </LegacyFilterToolbar>

                            {loading ? (
                                <Loaders />
                            ) : (
                                <CommonTable
                                    columns={tableColumns}
                                    data={filteredData}
                                    isActionRequired={false}
                                />
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

NotInterestedReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(NotInterestedReport);
